import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { App, Button, Tag } from 'antd'

import DataTableX from 'components/DataTableX/DataTableX'
import SelectForm from 'components/SelectForm/SelectForm'
import useGenericInfo from 'hooks/useGenericInfo'
import {
    ConfiguracionInicialProvider,
} from './context/ConfiguracionInicialContext'
import useConfiguracionInicial from './hooks/useConfiguracionInicial'
import ModalConfiguracionInicial from './components/ModalConfiguracionInicial'
import ButtonMenu from 'components/ButtonMenu/ButtonMenu'
import iconoInformacion from 'assets/images/datatable/informacion.svg';
import iconoEditar from 'assets/images/datatable/editar.svg';
import { formatearFecha, formatosFecha } from 'util/formatDate'
import iconoInactivarActivar from 'assets/images/datatable/inactivar-activar.svg';
import { obtenerPermisosByPath } from 'util/funciones'
import Storage from "util/store/Storage";
import ModalConfirmacion from 'app/vistas/Tienda/Productos/components/ModalConfirmacionProduct'
import { ConfigInicialRow, ESTADOS_CONFIG } from './interfaces/ConfiguracionInicial.interfaces'
import ConfigInicialIcon from './assets/icono_configuracion_inicial.svg'

const ConfiguracionInicialContent = () => {
    const { usuario } = Storage.getState()
    const location = useLocation();
    const permisos: string = useMemo(() => obtenerPermisosByPath(usuario, location.pathname), [usuario, location])

    const { organizationByUser, getEmpresasByOrganizacion, empresasByOrganizacion } = useGenericInfo({ organizations: true })
    const [configSelected, setConfigSelected] = useState<ConfigInicialRow|null>(null)
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false)

    const {
        filteredRows,
        loadingTable,
        openNew,
        fetchListado,
        toggleEstadoConfiguracion,
        openEdit,
        openView
    } = useConfiguracionInicial()
    const { notification } = App.useApp()

    const [form, setForm] = React.useState({
        organizacionSelected: null,
        empresaSelected: null,
    })

    const renderButtonMenu = (record: ConfigInicialRow) => {
        return [
            {
                label: 'Información',
                icon: <img src={iconoInformacion} />,
                onClick: () => {
                    openView(record)
                },
            },
            {
                label: 'Editar',
                icon: <img src={iconoEditar} />,
                onClick: () => {
                    openEdit(record)
                },
                disabled: record.estado_id == ESTADOS_CONFIG.INACTIVO
            },
            {
                label: record.estado_id == ESTADOS_CONFIG.INACTIVO ? 'Activar' : 'Inactivar',
                icon: <img src={iconoInactivarActivar} />,
                onClick: async () => {
                    setConfigSelected(record)
                    setOpenModalConfirm(true)
                },
                disabled: !permisos.includes('D')
            }
        ]
    }

    const COLUMNAS_CONFIG_INICIAL: any = [
        {
            dataIndex: 'identificador',
            align: 'center',
            title: 'Identificador',
            render: (value: string) => <span className="mono">{value}</span>,
        },
        {
            dataIndex: 'numero_pantalla',
            align: 'center',
            title: 'Pantalla',
            render: (value: number) => `#${value}`,
        },
        {
            dataIndex: 'caras',
            align: 'center',
            title: 'Caras',
            render: (value: string) => `${value}`,
        },
        {
            dataIndex: 'equipo_id',
            align: 'center',
            title: 'Equipo',
            render: (value: number) => `${value}`,
        },
        {
            dataIndex: 'fecha_creacion',
            align: 'center',
            title: 'Fecha Creación',
            render: (value: string) => `${formatearFecha(value, formatosFecha.completaServidor)}`,
        },
        {
            dataIndex: 'estado_id',
            align: 'center',
            title: 'Estado',
            render: (value: number) => (
                <Tag color={value === ESTADOS_CONFIG.ACTIVO ? 'success' : 'error'}>
                    {value === ESTADOS_CONFIG.ACTIVO ? 'Activo' : 'Inactivo'}
                </Tag>
            ),
        },
        {
            dataIndex: 'acciones',
            align: 'center',
            title: 'Acciones',
            render: (_: any, record: any) =>
                <section style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center'
                }} className='w-100'>
                        {<ButtonMenu itemsMenu={renderButtonMenu(record)} />}
                </section >
        },
    ]

    const onChangeOrganizacion = (value: any) => {
        setForm({ ...form, organizacionSelected: value, empresaSelected: null })
        getEmpresasByOrganizacion(value)
    }

    const handleConfirmAction = async (record: ConfigInicialRow | null) => {
        if (!record) return

        const result = await toggleEstadoConfiguracion(record)
        setOpenModalConfirm(false)
        setConfigSelected(null)

        if (result.success) {
            notification.success({
                message: 'Éxito',
                description: result.message,
            })
        } else {
            notification.warning({
                message: 'Advertencia',
                description: result.message ?? 'No se pudo actualizar el estado de la configuración.',
            })
        }
    }

    return (        
        <div>
            <ModalConfirmacion
                logo={ConfigInicialIcon}
                confirmText="SÍ"
                message={`¿Está seguro de que desea ${configSelected?.estado_id == ESTADOS_CONFIG.INACTIVO ? "activar" : "inactivar"} esta configuración?`}
                openModal={openModalConfirm}
                onClose={() => setOpenModalConfirm(false)}
                onConfirm={() => handleConfirmAction(configSelected)}
            />
            <DataTableX
                iconTable={<img src={ConfigInicialIcon} alt="icono" className="icon-table" />}
                titleTable="Conf. Inicial"
                nameReport="configuracion_inicial"
                loadingTable={loadingTable}
                data={filteredRows}
                columns={COLUMNAS_CONFIG_INICIAL}
                extraFilter={true && (
                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <SelectForm
                            style={{ flex: 1 }}
                            allowClear
                            placeholder="Seleccione su organización"
                            options={organizationByUser}
                            onChange={onChangeOrganizacion}
                            value={form.organizacionSelected}
                        />
                        {form.organizacionSelected && (
                            <SelectForm
                                style={{ flex: 1 }}
                                allowClear
                                placeholder="Seleccione su empresa"
                                options={empresasByOrganizacion}
                                onChange={(value) => {
                                    setForm({ ...form, empresaSelected: value })
                                    fetchListado(value)
                                }}
                                value={form.empresaSelected}
                            />
                        )}
                    </div>
                )}
                extraHeader={
                    <div className="section-extra-buttons" style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <Button
                            type="primary"
                            className="btn-new"
                            style={{
                                borderRadius: '12px',
                                width: '100%',
                                color: 'black',
                                fontWeight: 'bold',
                            }}
                            onClick={openNew}
                        >
                            +Nueva Configuración
                        </Button>
                    </div>
                }
            />

            <ModalConfiguracionInicial />
        </div>
    )
}

const ConfiguracionInicial = () => (
    <ConfiguracionInicialProvider>
        <ConfiguracionInicialContent />
    </ConfiguracionInicialProvider>
)

export default ConfiguracionInicial